/**
 * PSD export — "Export for Editing", brief 03-procreate.md + 06-full-flow.md.
 *
 * Layer stack (bottom→top; ag-psd `children[0]` is the BOTTOM layer, per the
 * PSD format's bottom-to-top layer records — do not assume children[0] is top):
 *   Background colour · Sketch reference · Ink reference · Escherllate Data
 *   (global QR, LOCKED) · then ONE Procreate Layer Group per tile instance.
 *   The QR sits BELOW the paintable groups and is locked so the artist can't
 *   select or paint on it by accident; it lives in the reserved data strip
 *   below the artwork, which no paint layer covers, so it stays visible there.
 *
 * Each instance group (brief "Layer Schema" v2 — Window/stencil) contains,
 * bottom→top: `Paint` (unlocked — the artist's layer) · `Window` (locked):
 * opaque everywhere except a transparent hole matching the tile shape, so
 * everything beneath it is covered outside the hole with no per-layer clipping ·
 * `Outline (white)` · `Outline (black)` (both locked, hidden by default, TOP):
 * two single-colour tile-separation guides above the Window so they read as the
 * finished edge (brief 14 §1). The Window's opaque region also carries the baked
 * instruction text + the identity QR, so identity + structure carry the machine
 * meaning, NOT the group name (Procreate copies names verbatim on duplicate, so
 * names are cosmetic — see psdSchema.ts).
 *
 * Viewport: the canvas is mapped from a bounding box that is the union of the
 * selected traces' ACTUAL drawn geometry (bezier bulge included) with the unit
 * cell [0,1]², so a trace that pokes outside the fundamental domain is captured
 * fully (fixing the old unit-cell clip) while the [0,1]² reference layers stay
 * visible. One shared tile-space→pixel transform drives every renderer, so all
 * layers register. No traces selected → the unit cell alone (synthetic 'Tile').
 *
 * *Symmetry* metadata (wpg/lattice/artist-tile/colour count) rides in the global
 * QR data layer (survives the Procreate round-trip) and in XMP
 * (`imageResources.xmpMetadata`, non-load-bearing cross-check). Whether Procreate
 * preserves group structure, lock flags, and hidden layers on its own re-export
 * needs on-device confirmation (same caveat class as PSD mask preservation).
 */
import { writePsd, type Layer, type Psd } from 'ag-psd';
import { compose, scale, translation, type Mat } from '@/geometry/affine';
import { downloadBlob } from '@/services/download';
import { exportFilename } from '@/services/exportFilename';
import { encodeToMatrices, matrixToRGBA, serializeData, type EscherllateData } from '@/services/psd/psdData';
import {
  OUTLINE_BLACK_LAYER,
  OUTLINE_WHITE_LAYER,
  PAINT_LAYER,
  WINDOW_LAYER,
  groupName,
  instructionText,
  serializeInstance,
  type InstanceDescriptor,
} from '@/services/psd/psdSchema';
import { serializeRenderMetadata } from '@/services/psd/psdMetadata';
import { blitTiledInto, type RasterKind } from '@/services/tileRenderer';
import { useLayersStore } from '@/stores/layers';
import { usePatternStore } from '@/stores/pattern';
import { useSketchbookStore } from '@/stores/sketchbook';
import { useToolsStore } from '@/stores/tools';
import type { TraceLayerData } from '@/types/drawing';

/** Reserved band below the artwork for the QR data layer(s) (brief §2). Fixed
 *  (not payload-derived) so the full canvas height — and thus the payload's own
 *  canvasW/H sanity fields — are known before encoding, no circularity. */
const DATA_STRIP_MIN = 200;
const DATA_STRIP_FRAC = 0.14;
const DATA_LABEL = 'Escherllate data — do not edit or delete this layer.';
/** prefix for the QR data layer(s); distinct from Paint - / Outline - so the
 *  importer's matchers ignore them. */
const DATA_PREFIX = 'Escherllate Data - ';

export interface PsdExportOptions {
  /** Trace-layer ids selected in the export dialog's checklist. */
  regionIds: string[];
  /** Longest-edge resolution in pixels, e.g. 2048. The canvas may be non-square
   *  (it matches the trace bounding box's aspect); the longer edge = this. */
  resolution: number;
}

/** Tile-separation outline: two separate single-colour layers (black + white),
 *  both hidden by default and stacked above the Window so they read as the
 *  finished tile edge. The artist switches on whichever contrasts with their
 *  artwork (brief 14 §1). Stroke is pixel-constant regardless of resolution. */
const OUTLINE_WHITE = '#f5f1ff';
const OUTLINE_BLACK = '#141019';
const OUTLINE_PX = 2.5;

/** A little breathing room around the bbox so an edge-hugging outline stroke
 *  isn't clipped by the canvas bounds. Fraction of the larger bbox dimension. */
const VIEW_MARGIN_FRAC = 0.03;

// ── Viewport / coordinate mapping ────────────────────────────────────────────

interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface ExportView {
  width: number;
  /** full canvas height (px) = artworkHeight + any reserved data strip. */
  height: number;
  /** the height (px) of the artwork region `toPixels` maps into (top of canvas);
   *  equals `height` when there's no data strip. */
  artworkHeight: number;
  /** tile-space → pixel-space (SVG-order affine). `density` is its scale. */
  toPixels: Mat;
  density: number;
}

const UNIT_CELL: BBox = { minX: 0, minY: 0, maxX: 1, maxY: 1 };

function unionBBox(a: BBox, b: BBox): BBox {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** BBox of the traces' actual geometry, conservatively including bezier bulge:
 *  every cubic segment lies within the convex hull of its four control points,
 *  so the union of each node's position and its two handle control points
 *  bounds the whole curve. Null if the selected traces have no node geometry
 *  (e.g. legacy display-only ink paths). */
function traceGeometryBBox(regions: readonly TraceLayerData[]): BBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;
  for (const r of regions) {
    for (const n of r.nodes) {
      const pts: [number, number][] = [
        [n.x, n.y],
        [n.x + n.inX, n.y + n.inY],
        [n.x + n.outX, n.y + n.outY],
      ];
      for (const [px, py] of pts) {
        any = true;
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
    }
  }
  return any ? { minX, minY, maxX, maxY } : null;
}

function computeView(bbox: BBox, resolution: number): ExportView {
  const rawW = bbox.maxX - bbox.minX;
  const rawH = bbox.maxY - bbox.minY;
  const margin = VIEW_MARGIN_FRAC * Math.max(rawW, rawH);
  const minX = bbox.minX - margin;
  const minY = bbox.minY - margin;
  const w = rawW + 2 * margin;
  const h = rawH + 2 * margin;
  const density = resolution / Math.max(w, h);
  const width = Math.max(1, Math.round(w * density));
  const height = Math.max(1, Math.round(h * density));
  const toPixels = compose(scale(density, density), translation(-minX, -minY));
  return { width, height, artworkHeight: height, toPixels, density };
}

/** Grow the canvas downward by a reserved data strip; the artwork mapping
 *  (`toPixels`, `artworkHeight`) is unchanged, so artwork stays in the top
 *  region and the strip below is free for the QR data layers. */
function withDataStrip(view: ExportView, stripHeight: number): ExportView {
  return { ...view, height: view.artworkHeight + stripHeight };
}

// ── Canvas helpers (view-aware) ──────────────────────────────────────────────

function viewCanvas(view: ExportView): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = view.width;
  canvas.height = view.height;
  return { canvas, ctx: canvas.getContext('2d')! };
}

function setViewTransform(ctx: CanvasRenderingContext2D, view: ExportView): void {
  const m = view.toPixels;
  ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
}

/** Render a reference layer as the tiled, symmetry-transformed, colour-mapped
 *  neighbour context filling the artwork region (brief §7), clipped so it never
 *  spills into the QR data strip below. `rings` is derived from the view's
 *  tile-space extent so the whole bbox is covered. */
function renderTiledReference(view: ExportView, kind: RasterKind): HTMLCanvasElement {
  const { canvas, ctx } = viewCanvas(view);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, view.width, view.artworkHeight);
  ctx.clip();
  const rings = Math.ceil(Math.max(view.width, view.artworkHeight) / view.density) + 1;
  blitTiledInto(ctx, view.toPixels, rings, kind);
  ctx.restore();
  return canvas;
}

/** Current canvas background colour (the value TileCanvas clears to). */
function canvasBgColour(): string {
  if (typeof document === 'undefined') return '#14121b';
  const v = getComputedStyle(document.documentElement).getPropertyValue('--canvas-bg').trim();
  return v || '#14121b';
}

// ── Layer rasters ────────────────────────────────────────────────────────────

function renderBackground(view: ExportView, colour: string): HTMLCanvasElement {
  const { canvas, ctx } = viewCanvas(view);
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, view.width, view.height);
  return canvas;
}

/** Tiled sketch-only context: the surrounding cells the artist would see when
 *  the tile repeats, so they can paint up to the boundary without seams (§7). */
function renderSketchReference(view: ExportView): HTMLCanvasElement {
  return renderTiledReference(view, 'sketch');
}

/**
 * Any visible sketch layer with content — hand-drawn layers are checked via
 * their touch-count buffer AND their frozen colour paint groups (brief
 * 10-colours — a layer drawn entirely with real colour has an empty legacy
 * buffer but real content in its groups); PSD-imported layers have no count
 * buffer (calling `getLayerCounts` on one would lazily register a blank
 * buffer and expose it to the next theme re-render), so an imported layer's
 * presence alone counts.
 */
function hasVisibleSketchContent(): boolean {
  const layers = useLayersStore();
  return layers.sketchLayers.some((l) => {
    if (!l.visible) return false;
    if (layers.hasSketchGroups(l.id)) return true;
    if (!layers.hasCountBuffer(l.id)) return true; // imported artwork — presence is enough
    return layers.getLayerCounts(l.id).some((c) => c > 0);
  });
}

/** Tiled ink-only context (§7) — same rationale as the sketch reference. */
function renderInkReference(view: ExportView): HTMLCanvasElement {
  return renderTiledReference(view, 'ink');
}

function hasVisibleInkContent(): boolean {
  const layers = useLayersStore();
  return layers.inkLayers.some((l) => l.visible && l.opacity > 0);
}

/** Stroke a path once in a single colour at a pixel-constant width. */
function strokeMono(ctx: CanvasRenderingContext2D, view: ExportView, colour: string, path: () => void): void {
  setViewTransform(ctx, view);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = colour;
  ctx.lineWidth = OUTLINE_PX / view.density;
  path();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

function renderRegionOutline(region: TraceLayerData, view: ExportView, colour: string): HTMLCanvasElement {
  const { canvas, ctx } = viewCanvas(view);
  if (!region.d) return canvas;
  const p = new Path2D(region.d);
  strokeMono(ctx, view, colour, () => ctx.stroke(p));
  return canvas;
}

function renderPolygonOutline(polygon: [number, number][], view: ExportView, colour: string): HTMLCanvasElement {
  const { canvas, ctx } = viewCanvas(view);
  if (polygon.length < 2) return canvas;
  strokeMono(ctx, view, colour, () => {
    ctx.beginPath();
    ctx.moveTo(polygon[0]![0], polygon[0]![1]);
    for (let i = 1; i < polygon.length; i++) ctx.lineTo(polygon[i]![0], polygon[i]![1]);
    ctx.closePath();
    ctx.stroke();
  });
  return canvas;
}

/** Opaque structural cover for the Window's outside-the-hole region. Colour is
 *  arbitrary — only alpha is load-bearing — so this is chosen to read as "guide
 *  chrome, not artwork"; the baked text uses a contrasting light ink. */
const WINDOW_COVER = '#2a2733';
const WINDOW_TEXT = '#f5f1ff';

/** Wrap `text` to `maxW`, drawing each line at `lineH` spacing from (x,y). */
function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lineH: number): void {
  let line = '';
  let yy = y;
  for (const word of text.split(' ')) {
    const test = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(test).width > maxW) {
      ctx.fillText(line, x, yy);
      line = word;
      yy += lineH;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}

/** Bake the identity QR + instruction/identifier text into the Window's opaque
 *  strip band (guaranteed outside the tile hole, with room — the margin around
 *  the hole is too thin). The QR is black-on-white (its own quiet zone) so a
 *  scanner finds it against the cover; text sits to its left. */
function bakeWindowChrome(ctx: CanvasRenderingContext2D, view: ExportView, text: string, desc: InstanceDescriptor): void {
  const stripTop = view.artworkHeight;
  const pad = 12;
  const qr = matrixToRGBA(encodeToMatrices(serializeInstance(desc))[0]!, 4, 4);
  const qx = Math.max(pad, view.width - qr.width - pad);
  const qy = stripTop + pad;
  const id = ctx.createImageData(qr.width, qr.height);
  id.data.set(qr.data);
  ctx.putImageData(id, qx, qy); // raw write — opaque QR field over the cover
  ctx.fillStyle = WINDOW_TEXT;
  ctx.font = '15px sans-serif';
  ctx.textBaseline = 'top';
  wrapText(ctx, text, pad, stripTop + pad, qx - pad * 2, 20);
}

/** Hard-threshold the alpha channel to 0/255 so the Window is opaque everywhere
 *  except a crisp hole — no partial-opacity pixels (at the hole's AA edge or a
 *  glyph edge) that would faintly leak buried artwork through the cover. */
function thresholdAlpha(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const img = ctx.getImageData(0, 0, w, h);
  const px = img.data;
  for (let i = 3; i < px.length; i += 4) px[i] = px[i]! < 128 ? 0 : 255;
  ctx.putImageData(img, 0, 0);
}

/** The `Window` stencil (v2): opaque cover everywhere, with a transparent hole
 *  punched to the tile shape (`drawShape`, in tile space). Bakes the chrome
 *  first, then punches the hole, then hard-thresholds alpha. */
function renderWindow(
  view: ExportView,
  drawShape: (ctx: CanvasRenderingContext2D) => void,
  desc: InstanceDescriptor,
  bakedText: string,
): HTMLCanvasElement {
  const { canvas, ctx } = viewCanvas(view);
  ctx.fillStyle = WINDOW_COVER;
  ctx.fillRect(0, 0, view.width, view.height);
  bakeWindowChrome(ctx, view, bakedText, desc);
  // Punch the hole: `destination-out` clears the shape region to transparent.
  ctx.save();
  setViewTransform(ctx, view);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = '#000';
  drawShape(ctx);
  ctx.restore();
  thresholdAlpha(ctx, view.width, view.height);
  return canvas;
}

// ── Layer wrappers ───────────────────────────────────────────────────────────

function plainLayer(name: string, canvas: HTMLCanvasElement): Layer {
  return { name, canvas, opacity: 1 };
}

function fullBounds(view: ExportView): Pick<Layer, 'top' | 'left' | 'right' | 'bottom'> {
  return { top: 0, left: 0, right: view.width, bottom: view.height };
}

/** Procreate "lock this layer" — blocks edits (and, per the brief, hopefully
 *  rename too; on-device-verified). ag-psd writes these flags; whether Procreate
 *  preserves them on its own re-export is an on-device gate. */
function lockFlags(): Pick<Layer, 'transparencyProtected' | 'protected'> {
  return { transparencyProtected: true, protected: { composite: true, position: true, transparency: true } };
}

// ── Instance-group children (brief "Layer Schema" v2, brief 14 §1) ──
// Bottom→top: Paint (artwork) · Window (stencil) · Outline white · Outline black.
// Both outlines sit ABOVE the Window so they read as the finished tile edge;
// both ship hidden so the artist switches on whichever contrasts with their
// artwork. The Window still covers everything beneath it outside its hole, so
// stack order among the artist's Paint layers stays free.

function outlineChild(view: ExportView, name: string, outlineCanvas: HTMLCanvasElement): Layer {
  return { name, ...fullBounds(view), canvas: outlineCanvas, opacity: 1, hidden: true, ...lockFlags() };
}

function paintChild(view: ExportView): Layer {
  const { canvas } = viewCanvas(view); // transparent — the artist paints here
  return { name: PAINT_LAYER, ...fullBounds(view), canvas, opacity: 1 };
}

function windowChild(view: ExportView, windowCanvas: HTMLCanvasElement): Layer {
  return { name: WINDOW_LAYER, ...fullBounds(view), canvas: windowCanvas, opacity: 1, ...lockFlags() };
}

/** A region to paint: a name, its two outline canvases (black + white), and a
 *  callback that fills its shape (in tile space) — used to punch the Window's
 *  hole. */
interface PaintRegion {
  name: string;
  outlineBlack: HTMLCanvasElement;
  outlineWhite: HTMLCanvasElement;
  drawShape: (ctx: CanvasRenderingContext2D) => void;
}

/** Render the QR data layer(s) into the reserved strip — one full-canvas layer
 *  per QR chunk (usually just one), each with its QR + a human "do not delete"
 *  label drawn in the band below the artwork. Metadata as pixels is the only
 *  channel that survives a Procreate round-trip (brief §2). */
function renderDataLayers(view: ExportView, payload: string): Layer[] {
  const quiet = 4;
  const matrices = encodeToMatrices(payload);
  const stripTop = view.artworkHeight;
  const stripH = view.height - view.artworkHeight;
  const labelH = 22;
  const pad = 10;
  const maxModules = Math.max(...matrices.map((m) => m.length + quiet * 2));
  const modulePx = Math.max(2, Math.min(8, Math.floor((stripH - labelH - pad * 2) / maxModules)));
  let x = pad;
  return matrices.map((m, i) => {
    const { canvas, ctx } = viewCanvas(view);
    ctx.fillStyle = '#000000';
    ctx.font = '14px sans-serif';
    ctx.textBaseline = 'top';
    ctx.fillText(DATA_LABEL, pad, stripTop + pad);
    const img = matrixToRGBA(m, modulePx, quiet);
    const id = ctx.createImageData(img.width, img.height);
    id.data.set(img.data);
    ctx.putImageData(id, x, stripTop + labelH + pad);
    x += img.width + pad;
    // Locked (and placed below the paintable groups by buildExportPsd) so the
    // artist can't select or paint on the QR by accident (brief 13-closer).
    return { ...plainLayer(`${DATA_PREFIX}${i + 1}`, canvas), ...lockFlags() };
  });
}

/** Build the ag-psd document (pure aside from canvas/DOM use) — split out from
 *  `exportPsd` so it can be unit-exercised without also invoking a download. */
export function buildExportPsd(opts: PsdExportOptions): Psd {
  const layersStore = useLayersStore();
  const patternStore = usePatternStore();
  const tools = useToolsStore();

  const traceRegions = opts.regionIds
    .map((id) => layersStore.findTraceLayer(id))
    .filter((r): r is TraceLayerData => r !== undefined);

  // Union the selected traces' real geometry with the unit cell: captures any
  // out-of-cell trace bulge (the clip-bug fix) while keeping the [0,1]²
  // reference layers on-canvas. No traces → the unit cell alone.
  const traceBox = traceGeometryBBox(traceRegions);
  const bbox = traceBox ? unionBBox(traceBox, UNIT_CELL) : UNIT_CELL;
  // Reserve the data strip up front so the full canvas height (and the payload's
  // own canvasW/H sanity fields) are fixed before encoding — no circularity.
  const stripHeight = Math.round(Math.max(DATA_STRIP_MIN, opts.resolution * DATA_STRIP_FRAC));
  const view = withDataStrip(computeView(bbox, opts.resolution), stripHeight);

  // Each selected trace → a paint region (outline + shape). `drawShape` fills
  // the region's silhouette in tile space; the Window uses it to punch its hole.
  // Nothing traced → a single whole-tile region (the fundamental polygon).
  const regions: PaintRegion[] =
    traceRegions.length > 0
      ? traceRegions.map((r) => ({
          name: r.name,
          outlineBlack: renderRegionOutline(r, view, OUTLINE_BLACK),
          outlineWhite: renderRegionOutline(r, view, OUTLINE_WHITE),
          drawShape: (ctx: CanvasRenderingContext2D) => {
            if (r.d) ctx.fill(new Path2D(r.d));
          },
        }))
      : [
          (() => {
            const polygon = patternStore.currentPattern.polygon();
            return {
              name: 'Tile',
              outlineBlack: renderPolygonOutline(polygon, view, OUTLINE_BLACK),
              outlineWhite: renderPolygonOutline(polygon, view, OUTLINE_WHITE),
              drawShape: (ctx: CanvasRenderingContext2D) => {
                ctx.beginPath();
                ctx.moveTo(polygon[0]![0], polygon[0]![1]);
                for (let i = 1; i < polygon.length; i++) ctx.lineTo(polygon[i]![0], polygon[i]![1]);
                ctx.closePath();
                ctx.fill();
              },
            };
          })(),
        ];

  // Colour tiling is one document-wide setting, so every region gets the same
  // instance count — one masked paint layer per colour instance.
  const instanceCount = Math.max(1, tools.colourInstancesNeeded);

  // children[0] = BOTTOM. Build bottom→top.
  const children: Layer[] = [];
  children.push(plainLayer('Background colour', renderBackground(view, canvasBgColour())));
  if (hasVisibleSketchContent()) children.push(plainLayer('Sketch reference', renderSketchReference(view)));
  if (hasVisibleInkContent()) children.push(plainLayer('Ink reference', renderInkReference(view)));

  // Authoritative metadata as QR pixel layers (survives the Procreate round-trip
  // where XMP does not — brief §2). Same payload also written to XMP below as a
  // non-load-bearing cross-check (§6). Placed here — below the paintable groups
  // and locked (renderDataLayers) — so the artist can't select or draw on it by
  // accident (brief 13-closer). It occupies the reserved data strip below the
  // artwork, which no paint layer covers, so stack order doesn't hide it.
  // Pixel rect of tile-space [0,1]² within the (padded, non-square) canvas, so
  // the importer can crop the paint back to the unit cell rather than tiling the
  // whole canvas. toPixels is scale+translate only (no rotation): map (0,0) and
  // (1,1) through it. m = [a, b, c, d, e, f]; x = a·u + c·v + e, y = b·u + d·v + f.
  const m = view.toPixels;
  const unitRect: [number, number, number, number] = [
    Math.round(m[4]),
    Math.round(m[5]),
    Math.round(m[0] + m[2] + m[4]),
    Math.round(m[1] + m[3] + m[5]),
  ];

  const data: EscherllateData = {
    v: 1,
    tileId: useSketchbookStore().currentName || 'tile',
    wpgId: patternStore.currentConfig.wpgId,
    latticeParams: patternStore.currentConfig.latticeParams,
    artistTile: patternStore.currentConfig.artistTile,
    colourCount: tools.colourInstancesNeeded,
    regions: regions.map((r) => r.name),
    canvasW: view.width,
    canvasH: view.height,
    unitRect,
  };
  children.push(...renderDataLayers(view, serializeData(data)));

  // ONE Procreate Layer Group per (shape, style) tile instance. The group name
  // is cosmetic (cascade); machine identity is the QR baked into the Window, so
  // duplicating a group in Procreate keeps its identity. 'pass through' is the
  // Procreate default group blend. Every style of a region shares the region's
  // Outline + shape (a colourway of the same shape). The first instance's Window
  // bakes the full instruction sentence; the rest bake just their group name.
  const styleCount = instanceCount;
  let firstInstance = true;
  regions.forEach((region, ri) => {
    for (let si = 0; si < styleCount; si++) {
      const desc: InstanceDescriptor = { shape: ri + 1, style: si + 1 };
      const name = groupName({ nickname: region.name, shape: desc.shape, styleCount, style: desc.style });
      const bakedText = firstInstance ? instructionText(name === 'PaintMe' ? null : name) : name;
      firstInstance = false;
      children.push({
        name,
        opened: false,
        blendMode: 'pass through',
        children: [
          paintChild(view),
          windowChild(view, renderWindow(view, region.drawShape, desc, bakedText)),
          outlineChild(view, OUTLINE_WHITE_LAYER, region.outlineWhite),
          outlineChild(view, OUTLINE_BLACK_LAYER, region.outlineBlack),
        ],
      });
    }
  });

  return {
    width: view.width,
    height: view.height,
    children,
    imageResources: {
      xmpMetadata: serializeRenderMetadata({
        v: 1,
        wpgId: patternStore.currentConfig.wpgId,
        latticeParams: patternStore.currentConfig.latticeParams,
        artistTile: patternStore.currentConfig.artistTile,
        colourCount: tools.colourInstancesNeeded,
      }),
    },
  };
}

export function exportPsd(opts: PsdExportOptions): void {
  const psd = buildExportPsd(opts);
  const buffer = writePsd(psd);
  downloadBlob(new Blob([buffer], { type: 'application/octet-stream' }), exportFilename('psd'));
}
